import React from "react";
import { VisuallyHidden } from "react-aria-components";
import { t } from "ttag";

import { FiftyWaysCardsBlock } from "@reactivated";

import { IconButton } from "../Clickables";
import { HLevel } from "../Headings";
import { Card } from "./Card";

import styles from "./FiftyWaysCards.module.scss";

const wrap = (i: number, count: number) =>
    count === 0 ? 0 : ((i % count) + count) % count;

// Target angular spacing between cards on the orbit. Smaller = more cards
const TARGET_STEP_DEG = 48;

export const FiftyWaysCards = ({ value }: { value: FiftyWaysCardsBlock }) => {
    const cardCount = value.cards?.length ?? 0;

    // Repeat the source cards around the full ring so visual density stays
    // consistent regardless of how many unique cards the editor entered.
    const repeats =
        cardCount === 0
            ? 0
            : Math.max(1, Math.ceil(360 / (TARGET_STEP_DEG * cardCount)));
    const slots = React.useMemo(
        () => Array.from({ length: repeats }, () => value.cards ?? []).flat(),
        [value.cards, repeats],
    );
    const slotCount = slots.length;
    const stepDeg = slotCount > 0 ? 360 / slotCount : 0;

    // Absolute, monotonic rotation of the orbit container. Negative = clockwise.
    // Tracking it as a counter (instead of `currentIndex * stepDeg`) lets the
    // CSS transition take the shortest path even when wrapping past 0/360°.
    const [rotation, setRotation] = React.useState(0);
    const [flippedIndex, setFlippedIndex] = React.useState<number | null>(null);

    // Focused slot = the one currently rotated up to angle 0 at the top of the arc.
    const focusedSlot =
        stepDeg === 0 ? 0 : wrap(Math.round(-rotation / stepDeg), slotCount);
    const focusedCard = cardCount > 0 ? focusedSlot % cardCount : 0;

    const goNext = React.useCallback(() => {
        if (slotCount < 2) return;
        setFlippedIndex(null);
        setRotation((r) => r - stepDeg);
    }, [slotCount, stepDeg]);

    const goPrev = React.useCallback(() => {
        if (slotCount < 2) return;
        setFlippedIndex(null);
        setRotation((r) => r + stepDeg);
    }, [slotCount, stepDeg]);

    const handleFlip = (slotIndex: number) => {
        if (slotIndex !== focusedSlot) return;
        setFlippedIndex((prev) => (prev === slotIndex ? null : slotIndex));
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            goPrev();
        } else if (e.key === "ArrowRight") {
            e.preventDefault();
            goNext();
        } else if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setFlippedIndex((prev) =>
                prev === focusedSlot ? null : focusedSlot,
            );
        }
    };

    if (cardCount === 0) return null;

    return (
        <HLevel>
            <section
                className={styles.root}
                role="group"
                aria-roledescription={t`carousel`}
                aria-label={t`50 Ways cards`}
                tabIndex={0}
                onKeyDown={handleKeyDown}
            >
                <div className={styles.stage}>
                    <div
                        className={styles.orbit}
                        style={{ transform: `rotate(${rotation}deg)` }}
                    >
                        {slots.map((card, i) => {
                            // Shortest signed distance from focused slot, wrapped on the ring.
                            const rawDist = i - focusedSlot;
                            const half = slotCount / 2;
                            const wrappedDist =
                                rawDist > half
                                    ? rawDist - slotCount
                                    : rawDist < -half
                                      ? rawDist + slotCount
                                      : rawDist;
                            const zIndex = slotCount - Math.abs(wrappedDist);
                            const onNavigate =
                                wrappedDist === -1
                                    ? goPrev
                                    : wrappedDist === 1
                                      ? goNext
                                      : undefined;
                            return (
                                <Card
                                    key={i}
                                    card={card}
                                    angleDeg={i * stepDeg}
                                    isFocused={i === focusedSlot}
                                    isFlipped={flippedIndex === i}
                                    isInert={Math.abs(wrappedDist) > 1}
                                    onFlip={() => handleFlip(i)}
                                    onNavigate={onNavigate}
                                    zIndex={zIndex}
                                />
                            );
                        })}
                    </div>
                </div>

                <div className={styles.controls}>
                    <IconButton
                        icon="caret"
                        label={t`Previous card`}
                        onPress={goPrev}
                    />
                    <IconButton
                        icon="caret"
                        label={t`Next card`}
                        onPress={goNext}
                    />
                </div>

                <VisuallyHidden aria-live="polite" aria-atomic="true">
                    {t`Showing card ${focusedCard + 1} of ${cardCount}`}
                </VisuallyHidden>
            </section>
        </HLevel>
    );
};
